import { Paper, Stack, Text } from "@mantine/core";
import { chunkItems, flattenContent } from "@/pages/ClientProfile/utils";
import LabelItem from "../items/LabelItem";
import ReviewItem from "../items/ReviewItem";
import DocumentItem from "../items/DocumentItem";
import DynamicItemsField from "../items/DynamicItemsField";
import React, { useMemo } from "react";
import DocumentScanFailed from "../../EmptyState/DocumentScanFailed";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";

interface SinglePageContentProps {
  contentData: Record<string, any>;
  classes: Record<string, string>;
  isVeryFirstPage?: boolean;
  refetchOCR?: () => Promise<void>;
}

export const SinglePageContent: React.FC<SinglePageContentProps> = React.memo(
  ({ contentData, classes, isVeryFirstPage = true, refetchOCR }) => {
    // Get form type from the content data
    const detectedFormType = contentData?.owner?.type || null;

    // Flatten the content data structure similar to SectionContent
    const allItems = useMemo(
      () => flattenContent(contentData, 0, detectedFormType),
      [contentData, detectedFormType]
    );
    const pages = useMemo(() => chunkItems(allItems), [allItems]);

    const formTypeFromData = contentData?.owner?.type ?? "Tax Form";
    const { headerData } = useProfileStore();
    const { review, selectedDoc } = useProfileStore();

    // Construct full name with proper fallback logic
    const firstName = headerData?.basicInfo?.firstName;
    const lastName = headerData?.basicInfo?.lastName;
    const fullName =
      firstName || lastName
        ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
        : "Client";

    if (allItems.length === 0) {
      if (review) {
        return <DocumentScanFailed docId={selectedDoc?.docId ?? null} />;
      }
      return (
        <Paper withBorder className={classes.emptyPaper}>
          <Text c="dimmed" size="sm" className={classes.sectionLabel}>
            General Information
          </Text>
          <Text size="sm" className={classes.italicText}>
            No data available.
          </Text>
        </Paper>
      );
    }

    return (
      <>
        {pages.map((pageItems, pageIndex) => {
          const isFirstPage = pageIndex === 0;
          const displayLabel = isFirstPage
            ? "General Information"
            : "General Information (Continued)";

          const showWelcome = isVeryFirstPage && pageIndex === 0;

          return (
            <React.Fragment key={`general-info-page-${pageIndex}`}>
              <Text
                c="dimmed"
                size="sm"
                ml={{ base: "10px", sm: "12px" }}
                className={
                  isFirstPage ? classes.sectionLabel : classes.continuedLabel
                }
              >
                {displayLabel}
              </Text>
              <Paper withBorder className={classes.paper}>
                {showWelcome && (
                  <Stack className={classes.fieldHeader}>
                    <Text fw={500}>{formTypeFromData}</Text>
                    <Text fw={500}>{fullName}</Text>
                  </Stack>
                )}
                <div className={classes.grid}>
                  {pageItems.map((item) => {
                    const gridColumnSpan =
                      item.type === "label" || item.type === "dynamic_items"
                        ? "1 / -1"
                        : "span 1";
                    return (
                      <div
                        key={item.key}
                        style={{
                          gridColumn: gridColumnSpan,
                          width: "100%",
                        }}
                      >
                        {item.type === "label" && <LabelItem item={item} />}
                        {item.type === "field" && <ReviewItem item={item} />}
                        {item.type === "document" && (
                          <DocumentItem item={item} />
                        )}
                        {item.type === "dynamic_items" && (
                          <DynamicItemsField
                            item={item}
                            formType={detectedFormType}
                            fieldKey={item.key.split(".").pop() || ""}
                            classes={classes}
                            refetchOCR={refetchOCR}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Paper>
            </React.Fragment>
          );
        })}
      </>
    );
  }
);

SinglePageContent.displayName = "SinglePageContent";
